import { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import {
  type CanonicalEntity,
  type CompanyDetail,
  type EnrichmentDetailEvent,
  type EnrichmentFactEvent,
  type EnrichmentFieldEvent,
  type EnrichmentOwnershipEvent,
  type EnrichmentProgressEvent,
  type EnrichmentResult,
  type EnrichmentToolEvent,
  type OwnershipInterest,
  type RelatedParty,
  type EntityType,
  type Identifier,
  type InferredField,
  type JurisdictionEntry,
  type Jurisdiction,
  type Metadata,
  type ProgressEvent,
  type SearchMode,
  type SearchResponse,
  enrichEntityStream,
  fetchPreferences,
  fetchMetadata,
  fetchSources,
  recordClick,
  savePreferences,
  search,
  searchDeepStream,
} from './api';
import { Link } from 'react-router-dom';
import { useAuth } from './contexts/useAuth';
import { BackIcon, GlobeIcon } from './components/icons';
import { ThemeToggle } from './components/ThemeToggle';
import { applyTheme, isThemeMode, readStoredTheme, type ThemeMode } from './theme';
import {
  ENTITY_SUBTYPE_LABELS,
  archetypeLabel,
  interestTypeLabel,
  labelFor,
  reportingExceptionLabel,
  setSourceLabels,
  sourceLabel,
} from './labels';
import './App.css';

// --- inline icons (no dep) -------------------------------------------------

function CompanyIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M3 21V7l9-4 9 4v14" />
      <path d="M9 21v-6h6v6" />
      <path d="M9 11h.01M15 11h.01M9 15h.01M15 15h.01" />
    </svg>
  );
}

function PersonIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <circle cx="11" cy="11" r="8" />
      <path d="M21 21l-4.3-4.3" />
    </svg>
  );
}

function FilterIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M4 6h16M7 12h10M10 18h4" />
    </svg>
  );
}

function ChevronDownIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

// --- helpers ---------------------------------------------------------------

type ContinentCode = 'ALL' | 'EU' | 'NA' | 'SA' | 'AS' | 'AF' | 'OC';
type CoverageMode = 'proven' | 'all';

const CONTINENTS: Array<{ code: ContinentCode; label: string }> = [
  { code: 'ALL', label: 'Global' },
  { code: 'EU', label: 'Europe' },
  { code: 'NA', label: 'North America' },
  { code: 'SA', label: 'South America' },
  { code: 'AS', label: 'Asia' },
  { code: 'AF', label: 'Africa' },
  { code: 'OC', label: 'Oceania' },
];

const CONTINENT_COUNTRIES: Record<Exclude<ContinentCode, 'ALL'>, Set<string>> = {
  EU: new Set([
    'AD', 'AL', 'AT', 'AX', 'BA', 'BE', 'BG', 'BY', 'CH', 'CY', 'CZ', 'DE',
    'DK', 'EE', 'ES', 'FI', 'FO', 'FR', 'GB', 'GG', 'GI', 'GR', 'HR', 'HU',
    'IE', 'IM', 'IS', 'IT', 'JE', 'LI', 'LT', 'LU', 'LV', 'MC', 'MD', 'ME',
    'MK', 'MT', 'NL', 'NO', 'PL', 'PT', 'RO', 'RS', 'RU', 'SE', 'SI', 'SJ',
    'SK', 'SM', 'UA', 'VA', 'XK',
  ]),
  NA: new Set([
    'AG', 'AI', 'AW', 'BB', 'BL', 'BM', 'BQ', 'BS', 'BZ', 'CA', 'CR', 'CU',
    'CW', 'DM', 'DO', 'GD', 'GL', 'GP', 'GT', 'HN', 'HT', 'JM', 'KN', 'KY',
    'LC', 'MF', 'MQ', 'MS', 'MX', 'NI', 'PA', 'PM', 'PR', 'SV', 'SX', 'TC',
    'TT', 'US', 'VC', 'VG', 'VI',
  ]),
  SA: new Set([
    'AR', 'BO', 'BR', 'CL', 'CO', 'EC', 'FK', 'GF', 'GY', 'PE', 'PY', 'SR',
    'UY', 'VE',
  ]),
  AS: new Set([
    'AE', 'AF', 'AM', 'AZ', 'BD', 'BH', 'BN', 'BT', 'CC', 'CN', 'CX', 'GE',
    'HK', 'ID', 'IL', 'IN', 'IO', 'IQ', 'IR', 'JO', 'JP', 'KG', 'KH', 'KP',
    'KR', 'KW', 'KZ', 'LA', 'LB', 'LK', 'MM', 'MN', 'MO', 'MV', 'MY', 'NP',
    'OM', 'PH', 'PK', 'PS', 'QA', 'SA', 'SG', 'SY', 'TH', 'TJ', 'TM', 'TR',
    'TW', 'UZ', 'VN', 'YE',
  ]),
  AF: new Set([
    'AO', 'BF', 'BI', 'BJ', 'BW', 'CD', 'CF', 'CG', 'CI', 'CM', 'CV', 'DJ',
    'DZ', 'EG', 'EH', 'ER', 'ET', 'GA', 'GH', 'GM', 'GN', 'GQ', 'GW', 'KE',
    'KM', 'LR', 'LS', 'LY', 'MA', 'MG', 'ML', 'MR', 'MU', 'MW', 'MZ', 'NA',
    'NE', 'NG', 'RE', 'RW', 'SC', 'SD', 'SH', 'SL', 'SN', 'SO', 'SS', 'ST',
    'SZ', 'TD', 'TF', 'TG', 'TN', 'TZ', 'UG', 'YT', 'ZA', 'ZM', 'ZW',
  ]),
  OC: new Set([
    'AS', 'AU', 'CK', 'FJ', 'FM', 'GU', 'HM', 'KI', 'MH', 'MP', 'NC', 'NF',
    'NR', 'NU', 'NZ', 'PF', 'PG', 'PN', 'PW', 'SB', 'TK', 'TO', 'TV', 'UM',
    'VU', 'WF', 'WS',
  ]),
};

function countryInContinent(code: string, continent: ContinentCode): boolean {
  if (continent === 'ALL') return true;
  return CONTINENT_COUNTRIES[continent].has(code);
}

function coverageText(entry: JurisdictionEntry | undefined, coverage: Set<Jurisdiction>): string {
  if (!entry) return 'Select a jurisdiction';
  if (entry.code === '*') return 'Web-grounded broad search';
  if (coverage.has(entry.code)) return 'Registry-backed coverage';
  return 'Web-grounded fallback';
}

function isContinentCode(value: unknown): value is ContinentCode {
  return CONTINENTS.some((item) => item.code === value);
}

function isCoverageMode(value: unknown): value is CoverageMode {
  return value === 'proven' || value === 'all';
}

function initialsFor(name: string | undefined, email: string): string {
  const source = name && name.trim() ? name : email.split('@')[0] ?? email;
  const words = source.replace(/[._-]+/g, ' ').split(' ').filter(Boolean);
  const initials = words.slice(0, 2).map((word) => word[0]?.toUpperCase()).join('');
  return initials || 'SX';
}

function primaryIdentifier(identifiers: Identifier[]): Identifier | undefined {
  // Inline row display: only show hard identifiers (dedupe=true). Source
  // links (e.g. Northdata URL) live in the detail view's source-links
  // section, not next to the result name.
  const candidates = identifiers.filter((i) => i.dedupe);
  if (candidates.length === 0) return undefined;
  return [...candidates].sort((a, b) => a.priority - b.priority)[0];
}

function entitySubtypeLabel(entity: CanonicalEntity): string {
  if (entity.entity_subtype && entity.entity_subtype in ENTITY_SUBTYPE_LABELS) {
    return ENTITY_SUBTYPE_LABELS[entity.entity_subtype];
  }
  return entity.entity_type.toLowerCase();
}

// --- meta + list components -----------------------------------------------

function MetaBlock({ meta }: { meta: SearchResponse['meta'] }) {
  const degraded =
    meta.sources_timed_out.length > 0 ||
    meta.sources_errored.length > 0 ||
    !meta.audit_recorded;
  return (
    <div className={`meta ${degraded ? 'meta--degraded' : ''}`}>
      <div>
        <strong>{meta.sources_responded}</strong> of{' '}
        <strong>{meta.sources_queried}</strong> sources responded
        {meta.from_cache && <span className="badge"> &nbsp;cached</span>}
        {!meta.audit_recorded && (
          <span className="badge badge--warn"> &nbsp;audit unavailable</span>
        )}
        <span className="latency"> · {meta.latency_ms} ms</span>
      </div>
      {meta.sources_timed_out.length > 0 && (
        <div className="meta-line">
          Timed out: {meta.sources_timed_out.map(sourceLabel).join(', ')}
        </div>
      )}
      {meta.sources_errored.length > 0 && (
        <div className="meta-line">
          Errored: {meta.sources_errored.map(sourceLabel).join(', ')}
        </div>
      )}
    </div>
  );
}

function ResultRow({
  entity,
  showJurisdiction,
  onActivate,
}: {
  entity: CanonicalEntity;
  showJurisdiction: boolean;
  onActivate: () => void;
}) {
  const primary = primaryIdentifier(entity.identifiers);
  return (
    <li>
      <button type="button" className="result" onClick={onActivate}>
        <div className="result-name-row">
          <span className="result-name">{entity.name}</span>
          {entity.possible_duplicate && (
            <span className="badge badge--warn">possible duplicate</span>
          )}
          {entity.confidence > 1 && (
            <span className="badge badge--ok">×{entity.confidence.toFixed(2)}</span>
          )}
        </div>
        <div className="result-meta-row">
          <span>
            <span className="label">source</span>
            <span className="value">{sourceLabel(entity.source)}</span>
          </span>
          {showJurisdiction && (
            <span>
              <span className="label">jurisdiction</span>
              <span className="value">{entity.jurisdiction}</span>
            </span>
          )}
          {primary && (
            <span>
              <span className="label">{primary.label}</span>
              <span className="value">{primary.value}</span>
            </span>
          )}
        </div>
      </button>
    </li>
  );
}

// --- detail view ----------------------------------------------------------

// --- inferred-field rendering (shared by search-time + enrichment) ---------

const ENRICHMENT_FIELDS: Array<{ key: string; label: string }> = [
  { key: 'official_website', label: 'Official website' },
  { key: 'vat_number', label: 'VAT number' },
  { key: 'registered_address', label: 'Registered address' },
  { key: 'phone', label: 'Phone' },
  { key: 'key_personnel', label: 'Key personnel' },
  { key: 'lei', label: 'LEI' },
];
const ENRICHMENT_FIELD_LABELS = new Map(ENRICHMENT_FIELDS.map((f) => [f.key, f.label]));

function enrichmentFieldLabel(field: string): string {
  return ENRICHMENT_FIELD_LABELS.get(field) ?? labelFor(field);
}

function evidenceToLink(evidence: string): { label: string; href?: string } {
  // Allow "URL :: excerpt" form (the enrichment pipeline emits this when a
  // citation is present). Falls back to plain code rendering otherwise.
  const sepIdx = evidence.indexOf(' :: ');
  const candidate = sepIdx > 0 ? evidence.slice(0, sepIdx) : evidence;
  if (candidate.startsWith('http://') || candidate.startsWith('https://')) {
    return { label: candidate, href: candidate };
  }
  return { label: evidence };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function formatStructuredAddress(value: Record<string, unknown>): string | null {
  const parts = [
    value.street,
    value.postal_code,
    value.city,
    value.region,
    value.country,
  ]
    .filter((v) => typeof v === 'string' && v.trim())
    .map((v) => String(v).trim());
  return parts.length > 0 ? parts.join(', ') : null;
}

function formatDisplayValue(value: unknown): string {
  if (Array.isArray(value)) return value.map(formatDisplayValue).filter(Boolean).join(', ');
  if (value === null || value === undefined) return '';
  if (isRecord(value)) {
    const address = formatStructuredAddress(value);
    if (address) return address;
    return Object.entries(value)
      .filter((entry) => entry[1] != null && entry[1] !== '')
      .map(([k, v]) => `${labelFor(k)}: ${formatDisplayValue(v)}`)
      .join(' · ');
  }
  return String(value);
}

function valueHref(value: string): string | undefined {
  return value.startsWith('http://') || value.startsWith('https://') ? value : undefined;
}

function toolLabel(toolId: string): string {
  return labelFor(toolId.replace(/_verify$/, '').replace(/_fetch$/, ''));
}

function EnrichedFactRow({ fact }: { fact: EnrichmentFactEvent }) {
  const value = formatDisplayValue(fact.value);
  const href = valueHref(value);
  const badgeText =
    fact.assurance === 'VERIFIED' && fact.verifier
      ? `Verified by ${toolLabel(fact.verifier)}`
      : ASSURANCE_LABEL[fact.assurance] ?? fact.assurance;
  return (
    <li className={`enriched-fact enriched-fact--${fact.assurance.toLowerCase()}`}>
      <div className="enriched-fact-main">
        <span className="enriched-fact-label">{enrichmentFieldLabel(fact.field)}</span>
        <span className="enriched-fact-value">
          {href ? (
            <a href={href} target="_blank" rel="noreferrer noopener">{value}</a>
          ) : (
            value || 'No value'
          )}
        </span>
        <span className={`assurance-badge assurance-badge--${fact.assurance.toLowerCase()}`}>
          {badgeText}
        </span>
        {/* Confidence is only meaningful for unverified AI guesses; verified
            and refuted facts are settled, so the % would be noise/contradictory. */}
        {fact.assurance === 'POTENTIAL' && fact.confidence > 0 && (
          <span className="enriched-confidence">{(fact.confidence * 100).toFixed(0)}%</span>
        )}
      </div>
      {fact.evidence.length > 0 && (
        <div className="inferred-evidence enriched-evidence">
          {fact.evidence.map((ev, j) => {
            const { label, href: evidenceHref } = evidenceToLink(ev);
            return evidenceHref ? (
              <a key={j} href={evidenceHref} target="_blank" rel="noreferrer noopener">
                {label}
              </a>
            ) : (
              <code key={j}>{label}</code>
            );
          })}
        </div>
      )}
    </li>
  );
}

// --- enrichment state machine ---------------------------------------------

type EnrichmentStatus = 'idle' | 'streaming' | 'partial' | 'done' | 'failed';

interface EnrichmentState {
  status: EnrichmentStatus;
  stage: string | null;       // last progress stage
  message: string | null;     // last progress message
  fields: InferredField[];    // collected via field events (legacy downcast)
  facts: Record<string, EnrichmentFactEvent>; // harness facts, latest per field
  activeTool: string | null;  // tool currently running ("calling VIES…")
  toolEvents: EnrichmentToolEvent[]; // full tool-run log (for the inspector)
  detail: CompanyDetail | null; // authoritative registry detail (harness)
  ownership: OwnershipInterest[]; // accumulated control edges (deduped)
  fromCache: boolean;
  generatedAt: string | null;
  error: string | null;
}

const INITIAL_ENRICHMENT: EnrichmentState = {
  status: 'idle',
  stage: null,
  message: null,
  fields: [],
  facts: {},
  activeTool: null,
  toolEvents: [],
  detail: null,
  ownership: [],
  fromCache: false,
  generatedAt: null,
  error: null,
};

// Dedup key for a control edge — controller identity (LEI when present, else
// name) × interest type × direction. Mirrors the backend's _edge_key so a
// streamed edge and its terminal-result twin collapse.
function edgeKey(edge: OwnershipInterest): string {
  const lei = edge.interested_party.identifiers.find((i) => i.scheme.toLowerCase() === 'lei')?.value;
  const who = (lei ?? edge.interested_party.name ?? '').trim().toLowerCase();
  // Lifecycle token mirrors the backend (_edge_key): a current vs historic
  // (ceased) edge to the same party/type/direction must not collapse. Undated
  // cessations fall back to the statement (start) date so two distinct historic
  // relationships to the same party stay apart.
  const lifecycle =
    edge.is_current === false
      ? `ceased:${edge.ended_date ?? edge.statement_date ?? ''}`
      : 'current';
  return `${who}|${edge.interest_type}|${edge.direct_or_indirect}|${lifecycle}`;
}

function mergeEdges(existing: OwnershipInterest[], incoming: OwnershipInterest[]): OwnershipInterest[] {
  const seen = new Set(existing.map(edgeKey));
  const merged = [...existing];
  for (const edge of incoming) {
    const key = edgeKey(edge);
    if (seen.has(key)) continue;
    seen.add(key);
    merged.push(edge);
  }
  return merged;
}

type EnrichmentAction =
  | { type: 'start' }
  | { type: 'progress'; ev: EnrichmentProgressEvent }
  | { type: 'field'; ev: EnrichmentFieldEvent }
  | { type: 'fact'; ev: EnrichmentFactEvent }
  | { type: 'tool'; ev: EnrichmentToolEvent }
  | { type: 'detail'; ev: EnrichmentDetailEvent }
  | { type: 'ownership'; ev: EnrichmentOwnershipEvent }
  | { type: 'result'; ev: EnrichmentResult }
  | { type: 'fail'; error: string };

function enrichmentReducer(state: EnrichmentState, action: EnrichmentAction): EnrichmentState {
  switch (action.type) {
    case 'start':
      return { ...INITIAL_ENRICHMENT, status: 'streaming' };
    case 'progress':
      return { ...state, stage: action.ev.stage, message: action.ev.message };
    case 'field':
      return { ...state, status: 'partial', fields: [...state.fields, action.ev.field] };
    case 'fact':
      return {
        ...state,
        status: 'partial',
        facts: { ...state.facts, [action.ev.field]: action.ev },
      };
    case 'tool':
      // Surface the live "calling VIES…" indicator; clear it when done. Also
      // retain every event for the data inspector's tool-run log.
      return {
        ...state,
        toolEvents: [...state.toolEvents, action.ev],
        activeTool:
          action.ev.status === 'started'
            ? action.ev.tool_id
            : state.activeTool === action.ev.tool_id
              ? null
              : state.activeTool,
      };
    case 'detail':
      // Authoritative registry detail streamed mid-run (before the terminal
      // result) so officers surface as soon as the registry returns.
      return {
        ...state,
        detail: action.ev.detail,
        ownership: mergeEdges(state.ownership, action.ev.detail.ownership ?? []),
      };
    case 'ownership':
      // Control edges streamed as a tool discovers them.
      return {
        ...state,
        status: 'partial',
        ownership: mergeEdges(state.ownership, action.ev.edges),
      };
    case 'result': {
      const failed = !!action.ev.error;
      const facts = { ...state.facts };
      for (const f of action.ev.facts ?? []) {
        facts[f.field] = {
          field: f.field,
          value: f.value,
          assurance: f.assurance,
          transition: f.assurance === 'VERIFIED' ? 'verified' : 'discovered',
          confidence: f.confidence,
          source: f.sources[0] ?? null,
          verifier: f.verifier,
          evidence: f.evidence,
        };
      }
      return {
        ...state,
        status: failed ? 'failed' : 'done',
        activeTool: null,
        fromCache: action.ev.from_cache,
        generatedAt: action.ev.generated_at,
        error: action.ev.error ?? null,
        detail: action.ev.detail ?? state.detail,
        ownership: mergeEdges(state.ownership, action.ev.detail?.ownership ?? []),
        facts,
        // If the result included a non-empty inferred list (typical for a
        // cache replay), prefer it -- we may have missed individual field
        // events on the cache path because they're replayed synchronously.
        fields: action.ev.inferred.length >= state.fields.length
          ? action.ev.inferred
          : state.fields,
      };
    }
    case 'fail':
      return { ...state, status: 'failed', error: action.error };
    default:
      return state;
  }
}

const ASSURANCE_LABEL: Record<string, string> = {
  POTENTIAL: 'AI-discovered',
  VERIFIED: 'Verified',
  REFUTED: 'Refuted',
  CONFLICTING: 'Conflicting',
};

// Shown ONLY on the AI-discovered (Online presence) group — never over the
// registry/verified data, which carries its own per-field provenance chip.
const AI_DISCLAIMER = 'AI-discovered — unverified. Verify before regulatory use.';

// Roles that denote ownership rather than an official organizational role.
// Split client-side until the backend's BODS ownership model lands.
const OWNER_ROLE_CODES = new Set(['SHAREHOLDER', 'BENEFICIAL_OWNER', 'SOLE_PROPRIETOR']);
const REGISTRATION_FACT_KEYS = ['vat_number', 'lei'];
const ONLINE_FACT_KEYS = ['official_website', 'phone'];
// GLEIF reporting-exception facts — rendered in the Ownership & control section,
// not as generic AI facts under Online presence.
const OWNERSHIP_EXCEPTION_KEYS = [
  'gleif_direct_parent_exception',
  'gleif_ultimate_parent_exception',
];
const PLACED_FACT_KEYS = new Set([
  ...REGISTRATION_FACT_KEYS,
  ...ONLINE_FACT_KEYS,
  ...OWNERSHIP_EXCEPTION_KEYS,
  'registered_address',
  'legal_form',
]);

type ProvKind = 'registry' | 'verified' | 'ai' | 'source';
interface Provenance {
  kind: ProvKind;
  label: string;
  confidence?: number;
}

// A small per-field chip carrying trust — Registry / Verified · GLEIF /
// AI-discovered · 95%. Confidence shows only for the (unsettled) AI kind.
function ProvenanceChip({ prov }: { prov: Provenance }) {
  const showPct = prov.kind === 'ai' && typeof prov.confidence === 'number' && prov.confidence > 0;
  return (
    <span className={`prov-chip prov-chip--${prov.kind}`}>
      {prov.label}
      {showPct ? ` · ${((prov.confidence ?? 0) * 100).toFixed(0)}%` : ''}
    </span>
  );
}

function ProfileRow({
  label,
  value,
  href,
  sub,
  prov,
  mono,
}: {
  label: string;
  value: string;
  href?: string;
  sub?: string;
  prov?: Provenance;
  mono?: boolean;
}) {
  return (
    <div className="profile-row">
      <span className="profile-row-label">{label}</span>
      <span className={`profile-row-value${mono ? ' profile-row-value--mono' : ''}`}>
        {href ? (
          <a href={href} target="_blank" rel="noreferrer noopener">
            {value}
          </a>
        ) : (
          value
        )}
        {sub ? <span className="profile-row-sub">{sub}</span> : null}
      </span>
      {prov ? <ProvenanceChip prov={prov} /> : null}
    </div>
  );
}

function PersonRow({ party }: { party: RelatedParty }) {
  const role = party.role_label || labelFor(party.role_canonical);
  const isOrg = party.party_kind === 'ORGANISATION';
  // Organisations (e.g. a corporate general partner) have no date of birth.
  const meta = [!isOrg && party.date_of_birth ? `b. ${party.date_of_birth}` : null, party.city]
    .filter(Boolean)
    .join(' · ');
  return (
    <div className="profile-person">
      <span className="profile-person-role">{role}</span>
      <span className="profile-person-name">
        {party.name}
        {isOrg ? <span className="profile-org-tag">Organisation</span> : null}
      </span>
      {meta ? <span className="profile-person-meta">{meta}</span> : null}
    </div>
  );
}

// One ownership/control edge: the controlling party, the nature of the
// interest, and a provenance chip. Edges arrive corroborating (POTENTIAL) from
// GLEIF, so they carry confidence and a source label rather than a hard claim.
// A natural-language description of one control edge — what kind of control and
// (only when a source provides it) the stake. Leads with the relationship, not
// the BODS jargon. Percentages are folded in when present, omitted otherwise.
// Render a percentage band from its bounds — source-agnostic, driven by data.
// Exact figure (min == max) → "30%" / "100%" — checked FIRST so a sole 100%
// owner reads "100%", never the illogical "100%+". Open-ended or top band (no
// max, or max ≥ 100) → "75%+". A closed band → "25–50%".
function formatPctBand(
  min: number | null | undefined,
  max: number | null | undefined,
): string | null {
  if (typeof min !== 'number') return null;
  if (max === min) return `${min}%`;
  if (typeof max !== 'number' || max >= 100) return `${min}%+`;
  return `${min}–${max}%`;
}

function describeInterest(edge: OwnershipInterest): string {
  const share = formatPctBand(edge.share_pct, edge.share_pct_max);
  const voting = formatPctBand(edge.voting_pct, edge.voting_pct_max);
  const stake = share ?? (voting ? `${voting} voting` : null);

  if (edge.interest_type === 'accountingConsolidationParent') {
    const tier = edge.direct_or_indirect === 'indirect' ? 'Ultimate parent' : 'Direct parent';
    return `${tier} · accounting consolidation`;
  }
  if (edge.interest_type === 'subsidiary') return 'Subsidiary';

  const base = interestTypeLabel(edge.interest_type);
  if (stake) return `Holds ${stake} · ${base.toLowerCase()}`;
  return base;
}

// Short source label for the edge chip — the group header already frames these
// as corroborating, so the chip just names the source (e.g. "GLEIF").
function edgeSourceLabel(source: string): string {
  const s = source.toLowerCase();
  if (s.startsWith('gleif')) return 'GLEIF';
  if (s.startsWith('companies_house')) return 'Companies House';
  return sourceLabel(source);
}

// One control edge, rendered party-first: WHO controls this company, HOW, and
// HOW SURE. The relationship phrase and provenance are secondary to the name.
function OwnershipEdgeRow({ edge }: { edge: OwnershipInterest }) {
  const controller = edge.interested_party;
  const lei = controller.identifiers.find((i) => i.scheme.toLowerCase() === 'lei')?.value;
  const name = controller.name || lei || 'Unnamed entity';
  const isOrg = controller.party_type !== 'PERSON';
  const addr = controller.address
    ? formatStructuredAddress(controller.address as unknown as Record<string, unknown>)
    : null;
  // Historic when the backend marks it not-current — independent of ended_date,
  // which can be null for a ceased edge with no recorded date.
  const ceased = edge.is_current === false;
  const verified = edge.assurance === 'VERIFIED';
  const prov: Provenance = {
    kind: verified ? 'verified' : 'ai',
    label: edgeSourceLabel(edge.source),
    confidence: edge.confidence,
  };
  return (
    <div className={`ownership-edge${ceased ? ' ownership-edge--ceased' : ''}`}>
      <div className="ownership-edge-head">
        <span className="ownership-edge-party">
          {controller.source_ref ? (
            <a href={controller.source_ref} target="_blank" rel="noreferrer noopener">
              {name}
            </a>
          ) : (
            name
          )}
          {controller.jurisdiction ? (
            <span className="ownership-edge-jur">{controller.jurisdiction}</span>
          ) : null}
          {isOrg ? <span className="profile-org-tag">Organisation</span> : null}
          {ceased ? (
            <span className="ownership-edge-ceased">
              {edge.ended_date ? `Ceased ${edge.ended_date}` : 'Ceased'}
            </span>
          ) : null}
        </span>
        <ProvenanceChip prov={prov} />
      </div>
      <span className="ownership-edge-rel">{describeInterest(edge)}</span>
      {addr ? <span className="ownership-edge-addr">{addr}</span> : null}
      {lei ? <span className="ownership-edge-id">LEI {lei}</span> : null}
    </div>
  );
}

// One collapsible raw-JSON block for the data inspector. Pretty-printed,
// scrollable; the source-of-truth view for technical users.
function JsonBlock({ label, data }: { label: string; data: unknown }) {
  return (
    <details className="json-block">
      <summary>{label}</summary>
      <pre>{JSON.stringify(data, null, 2)}</pre>
    </details>
  );
}

// Resolve the registered address to its best available source (registry detail
// > verified/AI fact > entity core field), with the matching provenance.
function bestAddress(
  entity: CanonicalEntity,
  detail: CompanyDetail | null,
  factByField: Record<string, EnrichmentFactEvent>,
): { value: string; prov: Provenance } | null {
  if (detail?.address) {
    const v = formatStructuredAddress(detail.address as Record<string, unknown>);
    if (v) return { value: v, prov: { kind: 'registry', label: 'Registry' } };
  }
  const fact = factByField['registered_address'];
  if (fact) {
    const v = formatDisplayValue(fact.value);
    if (v) {
      const prov: Provenance =
        fact.assurance === 'VERIFIED'
          ? { kind: 'verified', label: `Verified · ${toolLabel(fact.verifier ?? '')}` }
          : { kind: 'ai', label: 'AI-discovered', confidence: fact.confidence };
      return { value: v, prov };
    }
  }
  if (entity.address) {
    const v = formatStructuredAddress(entity.address as Record<string, unknown>);
    if (v) return { value: v, prov: { kind: 'registry', label: sourceLabel(entity.source) } };
  }
  if (entity.addresses.length > 0) {
    const v = formatDisplayValue(entity.addresses[0]);
    if (v) return { value: v, prov: { kind: 'source', label: sourceLabel(entity.source) } };
  }
  return null;
}

function CompanyProfile({
  entity,
  searchId,
  enrichmentAvailable,
}: {
  entity: CanonicalEntity;
  searchId: string;
  enrichmentAvailable: boolean;
}) {
  const [state, dispatch] = useReducer(enrichmentReducer, INITIAL_ENRICHMENT);
  const abortRef = useRef<AbortController | null>(null);
  useEffect(() => () => abortRef.current?.abort(), []);

  const run = async (force: boolean) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    dispatch({ type: 'start' });
    try {
      await enrichEntityStream(
        { result_token: entity.result_token, search_id: searchId, force, entity },
        {
          onProgress: (ev) => dispatch({ type: 'progress', ev }),
          onField: (ev) => dispatch({ type: 'field', ev }),
          onFact: (ev) => dispatch({ type: 'fact', ev }),
          onTool: (ev) => dispatch({ type: 'tool', ev }),
          onDetail: (ev) => dispatch({ type: 'detail', ev }),
          onOwnership: (ev) => dispatch({ type: 'ownership', ev }),
          onResult: (ev) => dispatch({ type: 'result', ev }),
          onError: (err) => dispatch({ type: 'fail', error: err.message }),
          signal: controller.signal,
        },
      );
    } catch (err) {
      if (controller.signal.aborted) return;
      dispatch({ type: 'fail', error: err instanceof Error ? err.message : String(err) });
    }
  };

  const isRunning = state.status === 'streaming' || state.status === 'partial';
  const showDone = state.status === 'done';

  // Unify harness facts + legacy inferred fields + search-time annotations into
  // one map (all unverified ones are POTENTIAL); enrichment facts win on a key.
  const factByField: Record<string, EnrichmentFactEvent> = { ...state.facts };
  const addAsPotential = (
    field: string,
    value: unknown,
    confidence: number,
    evidence: string[],
  ) => {
    if (factByField[field]) return;
    factByField[field] = {
      field,
      value,
      assurance: 'POTENTIAL',
      transition: 'discovered',
      confidence,
      source: null,
      verifier: null,
      evidence,
    };
  };
  if (Object.keys(state.facts).length === 0) {
    for (const inf of state.fields) {
      addAsPotential(inf.field, inf.value, inf.confidence, inf.source_evidence);
    }
  }
  for (const inf of entity.inferred) {
    addAsPotential(inf.field, inf.value, inf.confidence, inf.source_evidence);
  }
  const factFor = (key: string) => factByField[key];

  const detail = state.detail ?? entity.detail ?? null;
  const parties = detail?.parties ?? [];
  const officers = parties.filter((p) => !OWNER_ROLE_CODES.has(p.role_canonical));
  const owners = parties.filter((p) => OWNER_ROLE_CODES.has(p.role_canonical));
  // Control edges accumulate from streamed OwnershipEvents; merge the
  // (possibly pre-enriched) detail's edges to cover the cache/no-stream path.
  const ownershipEdges = mergeEdges(state.ownership, detail?.ownership ?? []);
  // Authoritative edges (VERIFIED — e.g. Companies House PSC, a primary
  // beneficial-ownership register) are NOT "corroborating": split them from the
  // GLEIF-style accounting-consolidation signals so each carries honest provenance.
  const authoritativeEdges = ownershipEdges.filter((e) => e.assurance === 'VERIFIED');
  const corroboratingEdges = ownershipEdges.filter((e) => e.assurance !== 'VERIFIED');
  const exceptionFacts = OWNERSHIP_EXCEPTION_KEYS.map(factFor).filter(
    Boolean,
  ) as EnrichmentFactEvent[];

  // Data inspector — the complete underlying data for technical users. The
  // canonical view drops `raw` (shown in its own block) to avoid duplication;
  // the enrichment snapshot is null until an enrichment run produces something.
  const { raw: upstreamPayload, ...canonicalEntity } = entity;
  const enrichmentSnapshot =
    state.status === 'idle'
      ? null
      : {
          status: state.status,
          from_cache: state.fromCache,
          generated_at: state.generatedAt,
          error: state.error,
          facts: Object.values(state.facts),
          // Ownership edges live inside `detail.ownership` (single source of
          // truth, mirroring the backend result payload) — not duplicated here.
          detail,
          tool_runs: state.toolEvents,
        };

  const identifiers = entity.identifiers
    .filter((i) => i.dedupe)
    .sort((a, b) => a.priority - b.priority);
  const sourceLinks = entity.identifiers.filter((i) => !i.dedupe);

  const legalForm = detail?.legal_form ?? entity.legal_form ?? null;
  const archetype = detail?.archetype ?? null;
  const legalFormProv: Provenance = detail?.legal_form
    ? { kind: 'registry', label: 'Registry' }
    : { kind: 'source', label: sourceLabel(entity.source) };

  const address = bestAddress(entity, detail, factByField);

  const registrationFacts = REGISTRATION_FACT_KEYS.map(factFor).filter(
    Boolean,
  ) as EnrichmentFactEvent[];
  const onlineFacts = ONLINE_FACT_KEYS.map(factFor).filter(Boolean) as EnrichmentFactEvent[];
  const leftoverFacts = Object.values(factByField).filter((f) => !PLACED_FACT_KEYS.has(f.field));
  const aiFacts = [...onlineFacts, ...leftoverFacts];

  const filledKeys = new Set(Object.keys(factByField));
  const unfilled = ENRICHMENT_FIELDS.filter((f) => !filledKeys.has(f.key));

  return (
    <>
      <section className="detail-section profile-section">
        <h3>Registration</h3>
        <div className="profile-rows">
          {identifiers.map((id, i) => (
            <ProfileRow
              key={`${id.scheme}-${i}`}
              label={id.label}
              value={id.value}
              mono
              sub={
                Object.keys(id.components).length > 0
                  ? Object.entries(id.components)
                      .map(([k, v]) => `${labelFor(k)}: ${v}`)
                      .join(' · ')
                  : undefined
              }
              prov={{ kind: 'registry', label: sourceLabel(entity.source) }}
            />
          ))}
          {legalForm ? (
            <ProfileRow
              label="Legal form"
              value={legalForm}
              sub={archetype ? archetypeLabel(archetype) : undefined}
              prov={legalFormProv}
            />
          ) : null}
          {detail?.incorporation_date ? (
            <ProfileRow
              label="Incorporated"
              value={detail.incorporation_date}
              prov={{ kind: 'registry', label: 'Registry' }}
            />
          ) : null}
          {detail?.status ? (
            <ProfileRow
              label="Status"
              value={labelFor(detail.status)}
              prov={{ kind: 'registry', label: 'Registry' }}
            />
          ) : null}
        </div>
        {registrationFacts.length > 0 ? (
          <ul className="detail-plain-list enriched-list">
            {registrationFacts.map((f) => (
              <EnrichedFactRow key={f.field} fact={f} />
            ))}
          </ul>
        ) : null}
      </section>

      {address ? (
        <section className="detail-section profile-section">
          <h3>Registered address</h3>
          <div className="profile-rows">
            <ProfileRow label="Address" value={address.value} prov={address.prov} />
          </div>
        </section>
      ) : null}

      {/* Management = officers; Ownership = owners/partners (person OR org). Kept
          as separate sections so a KG owned by holding companies doesn't list
          organisations under a "People" header. */}
      {officers.length > 0 ? (
        <section className="detail-section profile-section">
          <h3>
            Management
            <span className="prov-chip prov-chip--registry profile-section-chip">
              Official registry
            </span>
          </h3>
          <div className="profile-people">
            {officers.map((p, i) => (
              <PersonRow key={`off-${p.name}-${i}`} party={p} />
            ))}
          </div>
        </section>
      ) : null}
      {/* One "Ownership & control" home, split by provenance into two groups:
          register-declared owners (PersonRow) and corroborating external control
          edges (GLEIF today; PSC/registers later). Each row carries its own
          provenance, so the two coexist under a single header. */}
      {owners.length > 0 || ownershipEdges.length > 0 || exceptionFacts.length > 0 ? (
        <section className="detail-section profile-section">
          <h3>Ownership &amp; control</h3>

          {owners.length > 0 ? (
            <div className="ownership-group">
              <div className="ownership-group-label">
                From the commercial register
                <span className="prov-chip prov-chip--registry">Official registry</span>
              </div>
              <div className="profile-people">
                {owners.map((p, i) => (
                  <PersonRow key={`own-${p.name}-${i}`} party={p} />
                ))}
              </div>
            </div>
          ) : null}

          {authoritativeEdges.length > 0 ? (
            <div className="ownership-group">
              <div className="ownership-group-label">
                Persons with significant control · official register
                <span className="prov-chip prov-chip--registry">Official registry</span>
              </div>
              <div className="ownership-edges">
                {authoritativeEdges.map((e, i) => (
                  <OwnershipEdgeRow key={`auth-${edgeKey(e)}-${i}`} edge={e} />
                ))}
              </div>
            </div>
          ) : null}

          {corroboratingEdges.length > 0 || exceptionFacts.length > 0 ? (
            <div className="ownership-group">
              <div className="ownership-group-label">
                Corroborating · external sources
                <span className="prov-chip prov-chip--ai">Corroborating</span>
              </div>
              {corroboratingEdges.length > 0 ? (
                <div className="ownership-edges">
                  {corroboratingEdges.map((e, i) => (
                    <OwnershipEdgeRow key={`corr-${edgeKey(e)}-${i}`} edge={e} />
                  ))}
                </div>
              ) : null}
              {exceptionFacts.map((f) => (
                <div key={f.field} className="ownership-exception">
                  <span className="ownership-exception-text">
                    {reportingExceptionLabel(String(f.value))}
                  </span>
                  <span className="prov-chip prov-chip--verified">
                    {edgeSourceLabel(f.source ?? 'gleif')}
                  </span>
                </div>
              ))}
              <p className="profile-ai-note">
                Corroborating, not authoritative — accounting-consolidation signals from GLEIF
                Level-2 (who-owns-whom) data. Confirm against the primary register before
                regulatory use.
              </p>
            </div>
          ) : null}
        </section>
      ) : null}

      {aiFacts.length > 0 || enrichmentAvailable ? (
        <section className="detail-section profile-section profile-section--ai">
          <header className="profile-ai-header">
            <h3>Online presence</h3>
            <div className="enriched-actions">
              {enrichmentAvailable && state.status === 'idle' ? (
                <button type="button" className="enrich-button" onClick={() => run(false)}>
                  Enrich entity
                </button>
              ) : null}
              {isRunning ? (
                <button
                  type="button"
                  className="enrich-button enrich-button--cancel"
                  onClick={() => abortRef.current?.abort()}
                >
                  Cancel
                </button>
              ) : null}
              {enrichmentAvailable && (showDone || state.status === 'failed') ? (
                <button
                  type="button"
                  className="enrich-button"
                  onClick={() => run(true)}
                  title="Run a fresh enrichment, bypassing the 24h cache"
                >
                  Refresh
                </button>
              ) : null}
            </div>
          </header>

          {aiFacts.length > 0 ? (
            <>
              <p className="profile-ai-note">{AI_DISCLAIMER}</p>
              <ul className="detail-plain-list enriched-list">
                {aiFacts.map((f) => (
                  <EnrichedFactRow key={f.field} fact={f} />
                ))}
              </ul>
            </>
          ) : null}

          {enrichmentAvailable && state.status === 'idle' && aiFacts.length === 0 ? (
            <p className="enrich-hint">
              Surface website, VAT (VIES-verified for EU), address, phone, and LEI from public
              sources — registry officers stream in first.
            </p>
          ) : null}

          {isRunning ? (
            <>
              {state.activeTool ? (
                <div className="enriched-progress">Calling {toolLabel(state.activeTool)}…</div>
              ) : state.message ? (
                <div className="enriched-progress">{state.message}…</div>
              ) : null}
              <ul className="detail-plain-list enriched-list">
                {unfilled.map((f) => (
                  <li key={f.key} className="enriched-skeleton">
                    <span className="enriched-skeleton-label">{f.label}</span>
                    <span className="enriched-skeleton-bar" aria-hidden="true" />
                  </li>
                ))}
              </ul>
            </>
          ) : null}

          {state.status === 'failed' ? (
            <p className="enriched-error">
              Enrichment failed: {state.error ?? 'unknown error'}. Click Refresh to retry.
            </p>
          ) : null}

          {showDone ? (
            <p className="enriched-footer">
              {state.fromCache
                ? `Replayed from cache (generated ${state.generatedAt}). Refresh forces a fresh run.`
                : `Enriched ${state.generatedAt ?? ''}`}
            </p>
          ) : null}
        </section>
      ) : null}

      {sourceLinks.length > 0 ? (
        <section className="detail-section profile-section">
          <h3>Source links</h3>
          <ul className="detail-plain-list">
            {sourceLinks.map((s, i) => (
              <li key={`${s.scheme}-${i}`}>
                <span className="label">{s.label}</span>{' '}
                {s.value.startsWith('http') ? (
                  <a href={s.value} target="_blank" rel="noreferrer noopener">
                    {s.value}
                  </a>
                ) : (
                  <code>{s.value}</code>
                )}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {/* Data inspector — every field as the sources returned it, so a technical
          user can see provenance, what we got, and (by its absence in the JSON)
          what we didn't. Collapsed by default; unobtrusive for everyone else. */}
      <section className="detail-section detail-inspector">
        <h3>Data inspector</h3>
        <p className="inspector-note">
          The complete underlying data — what each source returned, and what it didn&apos;t.
        </p>
        <JsonBlock label="Canonical entity" data={canonicalEntity} />
        <JsonBlock label="Upstream payload (raw)" data={upstreamPayload} />
        {enrichmentSnapshot ? (
          <JsonBlock label="Enrichment result" data={enrichmentSnapshot} />
        ) : (
          <p className="inspector-empty">
            Run enrichment to inspect facts, ownership edges, and tool runs.
          </p>
        )}
      </section>
    </>
  );
}

function DetailPanel({
  entity,
  queryJurisdiction,
  searchId,
  enrichmentAvailable,
  onBack,
}: {
  entity: CanonicalEntity;
  queryJurisdiction: Jurisdiction;
  searchId: string;
  enrichmentAvailable: boolean;
  onBack: () => void;
}) {
  const showJurisdiction = entity.jurisdiction !== queryJurisdiction;
  const archetype = entity.detail?.archetype ?? null;

  return (
    <article className="detail">
      <div className="detail-toolbar">
        <button type="button" className="detail-back" onClick={onBack} aria-label="Back to results">
          <BackIcon /> Back to results
        </button>
      </div>

      <header className="detail-header">
        <h2>{entity.name}</h2>
        <div className="detail-badges">
          <span className="badge">{entitySubtypeLabel(entity)}</span>
          {archetype ? <span className="badge badge--ok">{archetypeLabel(archetype)}</span> : null}
          {showJurisdiction && <span className="badge">{entity.jurisdiction}</span>}
          <span className="badge">{sourceLabel(entity.source)}</span>
          {entity.possible_duplicate && (
            <span className="badge badge--warn">possible duplicate</span>
          )}
          {entity.confidence > 1 && (
            <span className="badge badge--ok">×{entity.confidence.toFixed(2)}</span>
          )}
        </div>
      </header>

      <CompanyProfile
        key={entity.result_token}
        entity={entity}
        searchId={searchId}
        enrichmentAvailable={enrichmentAvailable}
      />
    </article>
  );
}

// --- main app -------------------------------------------------------------

function App() {
  const { user, logout } = useAuth();
  const [metadata, setMetadata] = useState<Metadata | null>(null);
  const [metadataError, setMetadataError] = useState<string | null>(null);

  const [term, setTerm] = useState('');
  const [entityType, setEntityType] = useState<EntityType>('COMPANY');
  const [jurisdiction, setJurisdiction] = useState<Jurisdiction>('DE');
  const [region, setRegion] = useState<string | null>(null);
  const [mode, setMode] = useState<SearchMode>('QUICK');
  const [coverageMode, setCoverageMode] = useState<CoverageMode>('proven');
  const [continent, setContinent] = useState<ContinentCode>('EU');
  const [theme, setTheme] = useState<ThemeMode>(readStoredTheme);
  const requesterId = user?.email ?? 'anonymous';

  const [response, setResponse] = useState<SearchResponse | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [progress, setProgress] = useState<ProgressEvent | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const [preferencesReadyFor, setPreferencesReadyFor] = useState<string | null>(null);
  const accountRef = useRef<HTMLDivElement | null>(null);

  // Load /metadata on mount. We no longer narrow jurisdiction to
  // adapter-supported countries -- the full ISO list is now valid, with
  // adapter-less countries marked with "*" in the dropdown.
  useEffect(() => {
    fetchMetadata()
      .then((m) => {
        setMetadata(m);
      })
      .catch((err: Error) => setMetadataError(err.message));
  }, []);

  // Pull source display_names from the backend so sources.yaml stays the
  // single source of truth for human-readable labels.
  useEffect(() => {
    fetchSources()
      .then((list) => {
        setSourceLabels(list.map((s) => [s.name, s.display_name]));
      })
      .catch(() => {
        // Non-fatal: sourceLabel() falls back to a humanised slug.
      });
  }, []);

  useEffect(() => {
    let cancelled = false;

    fetchPreferences(requesterId)
      .then((prefs) => {
        if (cancelled) return;
        if (prefs.entity_type) setEntityType(prefs.entity_type);
        if (prefs.mode) setMode(prefs.mode);
        if (isCoverageMode(prefs.coverage_mode)) setCoverageMode(prefs.coverage_mode);
        if (isContinentCode(prefs.continent)) setContinent(prefs.continent);
        if (isThemeMode(prefs.theme)) setTheme(prefs.theme);
        if (prefs.jurisdiction) setJurisdiction(prefs.jurisdiction);
        setRegion(prefs.region ?? null);
      })
      .catch(() => {
        // Preferences are best-effort; the app defaults remain fully usable.
      })
      .finally(() => {
        if (!cancelled) setPreferencesReadyFor(requesterId);
      });

    return () => {
      cancelled = true;
    };
  }, [requesterId]);

  useEffect(() => {
    if (preferencesReadyFor !== requesterId) return;
    const timeout = window.setTimeout(() => {
      savePreferences(requesterId, {
        entity_type: entityType,
        jurisdiction,
        region,
        mode,
        coverage_mode: coverageMode,
        continent,
        theme,
      }).catch((err: unknown) => {
        console.warn('[searchx] preference save failed:', err);
      });
    }, 450);
    return () => window.clearTimeout(timeout);
  }, [continent, coverageMode, entityType, jurisdiction, mode, preferencesReadyFor, region, requesterId, theme]);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  useEffect(() => {
    if (!accountOpen) return undefined;
    const onPointerDown = (event: MouseEvent) => {
      if (!accountRef.current?.contains(event.target as Node)) {
        setAccountOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setAccountOpen(false);
    };
    window.addEventListener('mousedown', onPointerDown);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [accountOpen]);

  // Broad country list: every ISO country + the "*" wildcard. The legacy
  // `availability` map (adapter-supported only) is used to mark which
  // countries have a real registry adapter; everything else is web-only.
  const allCountries: JurisdictionEntry[] = useMemo(
    () => metadata?.all_jurisdictions ?? [],
    [metadata],
  );

  const adapterCoverage = useMemo(() => {
    if (!metadata) return new Set<Jurisdiction>();
    return new Set(metadata.availability[entityType] ?? []);
  }, [metadata, entityType]);

  const visibleCountries = useMemo(
    () =>
      allCountries.filter((country) => {
        if (!countryInContinent(country.code, continent)) return false;
        if (coverageMode === 'proven') return adapterCoverage.has(country.code);
        return true;
      }),
    [adapterCoverage, allCountries, continent, coverageMode],
  );

  // Currently selected country entry (for the region dropdown).
  const currentCountry = useMemo(
    () => allCountries.find((c) => c.code === jurisdiction),
    [allCountries, jurisdiction],
  );

  const coverageCounts = useMemo(() => {
    const scoped = allCountries.filter((country) => countryInContinent(country.code, continent));
    const proven = scoped.filter((country) => adapterCoverage.has(country.code)).length;
    const webOnly = scoped.filter(
      (country) => country.code !== '*' && !adapterCoverage.has(country.code),
    ).length;
    return { proven, webOnly, shown: visibleCountries.length };
  }, [adapterCoverage, allCountries, continent, visibleCountries.length]);

  const syncJurisdictionForScope = (
    nextEntityType: EntityType,
    nextCoverageMode: CoverageMode,
    nextContinent: ContinentCode,
  ) => {
    if (!metadata) return;
    const nextCoverage = new Set(metadata.availability[nextEntityType] ?? []);
    const nextCountries = allCountries.filter((country) => {
      if (!countryInContinent(country.code, nextContinent)) return false;
      if (nextCoverageMode === 'proven') return nextCoverage.has(country.code);
      return true;
    });
    if (nextCountries.some((country) => country.code === jurisdiction)) return;
    const preferred = nextCountries.find((country) => country.code === 'DE') ?? nextCountries[0];
    if (!preferred) return;
    setJurisdiction(preferred.code);
    setRegion(null);
  };

  const onEntityTypeChange = (next: EntityType) => {
    setEntityType(next);
    syncJurisdictionForScope(next, coverageMode, continent);
  };

  const onSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!term.trim() || pending) return;
    // Cancel any in-flight stream before starting a new search.
    abortRef.current?.abort();
    setPending(true);
    setError(null);
    setSelectedIndex(null);
    setProgress(null);
    // Clear previous results so the progress UI is visible during the
    // new search instead of being occluded by the prior result list.
    setResponse(null);
    const body = {
      term: term.trim(),
      entity_type: entityType,
      jurisdiction,
      region: region ?? null,
      requester_id: requesterId,
      limit: 25,
    };
    try {
      if (mode === 'DEEP') {
        // DEEP gets the SSE variant so we can render per-stage progress.
        const controller = new AbortController();
        abortRef.current = controller;
        await searchDeepStream(body, {
          signal: controller.signal,
          onProgress: (ev) => setProgress(ev),
          onResult: (r) => setResponse(r),
        });
      } else {
        const r = await search(mode, body);
        setResponse(r);
      }
    } catch (err) {
      // AbortError from a follow-up search is expected; don't surface it.
      const name = err instanceof Error ? err.name : '';
      if (name !== 'AbortError') {
        setError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setPending(false);
      setProgress(null);
      abortRef.current = null;
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      if (filtersOpen) {
        setFiltersOpen(false);
      } else {
        setTerm('');
      }
    }
  };

  const onResultActivate = (index: number) => {
    if (!response) return;
    const entity = response.results[index];
    setSelectedIndex(index);
    // PRD §9 click signal -- only when the response was audited; an
    // un-audited response has no search_results row for the token to
    // resolve against, so the endpoint would 404.
    if (response.meta.audit_recorded) {
      recordClick({
        result_token: entity.result_token,
        search_id: response.search_id,
        position: index,
        requester_id: requesterId,
      });
    }
  };

  const selectedEntity =
    response !== null && selectedIndex !== null ? response.results[selectedIndex] : null;
  const selectedCountryName = currentCountry?.name ?? jurisdiction;
  const selectedCountryWebOnly = currentCountry?.code === '*' || !adapterCoverage.has(jurisdiction);

  return (
    <div className="app">
      <header className="app-header">
        <div className="brand-lockup">
          <span className="brand-mark" aria-hidden="true">sx</span>
          <h1>searchx</h1>
          <p className="tagline">KYC/AML intelligence workspace</p>
        </div>
        {user && (
          <div className="account-menu" ref={accountRef}>
            <button
              type="button"
              className="account-trigger"
              aria-haspopup="menu"
              aria-expanded={accountOpen}
              onClick={() => setAccountOpen((open) => !open)}
            >
              {user.picture ? (
                <img src={user.picture} alt="" className="account-avatar" referrerPolicy="no-referrer" />
              ) : (
                <span className="account-avatar account-avatar--initials">
                  {initialsFor(user.name, user.email)}
                </span>
              )}
              <ChevronDownIcon />
            </button>
            {accountOpen && (
              <div className="account-dropdown" role="menu">
                <div className="account-card">
                  <strong>{user.name || 'SearchX user'}</strong>
                  <span>{user.email}</span>
                </div>
                <ThemeToggle
                  theme={theme}
                  onToggle={() => setTheme((current) => (current === 'dark' ? 'light' : 'dark'))}
                />
                <Link
                  to="/coverage"
                  role="menuitem"
                  className="account-action"
                  onClick={() => setAccountOpen(false)}
                >
                  <GlobeIcon />
                  Coverage
                </Link>
                <button
                  type="button"
                  role="menuitem"
                  className="account-action account-action--danger"
                  onClick={() => {
                    setAccountOpen(false);
                    logout();
                  }}
                >
                  Sign out
                </button>
              </div>
            )}
          </div>
        )}
      </header>

      <main className="workspace">
        <section className="command-surface" aria-label="Search controls">
          <form className={`searchbar ${filtersOpen ? 'searchbar--filters-open' : ''}`} onSubmit={onSubmit}>
            <div className="term-shell">
              <SearchIcon />
              <input
                type="text"
                className="term"
                placeholder="Search company or person"
                value={term}
                onChange={(e) => setTerm(e.target.value)}
                onKeyDown={onKeyDown}
                aria-label="Search term"
              />
            </div>

            <div className="search-controls">
              <div className="entity-toggle" role="radiogroup" aria-label="Entity type">
                <input
                  type="radio"
                  id="entity-company"
                  name="entity"
                  value="COMPANY"
                  checked={entityType === 'COMPANY'}
                  onChange={() => onEntityTypeChange('COMPANY')}
                />
                <label htmlFor="entity-company">
                  <CompanyIcon /> Company
                </label>
                <input
                  type="radio"
                  id="entity-person"
                  name="entity"
                  value="PERSON"
                  checked={entityType === 'PERSON'}
                  onChange={() => onEntityTypeChange('PERSON')}
                />
                <label htmlFor="entity-person">
                  <PersonIcon /> Person
                </label>
              </div>

              <div className="mode-toggle" role="radiogroup" aria-label="Search mode">
                <input
                  type="radio"
                  id="mode-quick"
                  name="mode"
                  value="QUICK"
                  checked={mode === 'QUICK'}
                  onChange={() => setMode('QUICK')}
                />
                <label htmlFor="mode-quick">Quick</label>
                <input
                  type="radio"
                  id="mode-deep"
                  name="mode"
                  value="DEEP"
                  checked={mode === 'DEEP'}
                  onChange={() => setMode('DEEP')}
                />
                <label htmlFor="mode-deep">Deep</label>
              </div>

              <button
                type="button"
                className={`filter-trigger ${filtersOpen ? 'filter-trigger--active' : ''}`}
                onClick={() => setFiltersOpen((open) => !open)}
                aria-expanded={filtersOpen}
              >
                <FilterIcon />
                {selectedCountryWebOnly && <span className="web-only-dot" aria-hidden="true" />}
                <span>{selectedCountryName}</span>
                <ChevronDownIcon />
              </button>
            </div>

            {filtersOpen && (
            <div className="jurisdiction-panel">
              <div className="panel-heading">
                <span><FilterIcon /> Jurisdiction</span>
                <strong>{coverageText(currentCountry, adapterCoverage)}</strong>
              </div>

              <div className="scope-toggle" role="radiogroup" aria-label="Country coverage">
                <input
                  type="radio"
                  id="coverage-proven"
                  name="coverage"
                  checked={coverageMode === 'proven'}
                  onChange={() => {
                    setCoverageMode('proven');
                    syncJurisdictionForScope(entityType, 'proven', continent);
                  }}
                />
                <label htmlFor="coverage-proven">
                  <span className="scope-label-main">Proven coverage</span>
                  <span className="scope-label-sub">{coverageCounts.proven} registry-backed</span>
                </label>
                <input
                  type="radio"
                  id="coverage-all"
                  name="coverage"
                  checked={coverageMode === 'all'}
                  onChange={() => {
                    setCoverageMode('all');
                    syncJurisdictionForScope(entityType, 'all', continent);
                  }}
                />
                <label htmlFor="coverage-all">
                  <span className="scope-label-main">Include web fallback</span>
                  <span className="scope-label-sub">+{coverageCounts.webOnly} web-only</span>
                </label>
              </div>

              <div className="continent-strip" aria-label="Continent filter">
                {CONTINENTS.map((item) => (
                  <button
                    key={item.code}
                    type="button"
                    className={continent === item.code ? 'active' : ''}
                    onClick={() => {
                      setContinent(item.code);
                      syncJurisdictionForScope(entityType, coverageMode, item.code);
                    }}
                  >
                    {item.label}
                  </button>
                ))}
              </div>

              <div className="select-row">
                <select
                  aria-label="Jurisdiction"
                  value={jurisdiction}
                  onChange={(e) => {
                    setJurisdiction(e.target.value);
                    setRegion(null);
                  }}
                  disabled={!metadata || visibleCountries.length === 0}
                >
                  {!metadata && <option value={jurisdiction}>{jurisdiction}</option>}
                  {visibleCountries.map((c) => {
                    const isWildcard = c.code === '*';
                    const webOnly = !adapterCoverage.has(c.code);
                    const label = isWildcard
                      ? 'Any country'
                      : webOnly
                        ? `○ ${c.name}`
                        : c.name;
                    return (
                      <option key={c.code} value={c.code}>
                        {label}
                      </option>
                    );
                  })}
                </select>

                {currentCountry && currentCountry.subdivisions.length > 0 && (
                  <select
                    aria-label="Region"
                    className="region-select"
                    value={region ?? ''}
                    onChange={(e) => setRegion(e.target.value || null)}
                  >
                    <option value="">Any region</option>
                    {currentCountry.subdivisions.map((s) => (
                      <option key={s.code} value={s.code}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>
            )}

            <button type="submit" className="submit" disabled={pending || !term.trim()}>
              <span className="submit-label">
                <SearchIcon />
                {pending ? 'Searching' : 'Run search'}
              </span>
            </button>
          </form>

        </section>

        <section className="results" aria-live="polite" aria-busy={pending && !response}>
        {metadataError && (
          <div className="error">Metadata unavailable: {metadataError}</div>
        )}
        {error && <div className="error">Error: {error}</div>}

        {pending && !response && (
          <div className="loading">
            <div className="loading-orbit" aria-hidden="true">
              <span />
              <span />
              <span />
            </div>
            <div>
              <div className="loading-text">
                {progress ? progress.message : 'Searching upstream sources'}
              </div>
              {progress && (
                <div className="loading-elapsed">
                  {(progress.elapsed_ms / 1000).toFixed(1)}s elapsed
                </div>
              )}
            </div>
          </div>
        )}

        {selectedEntity && response ? (
          <DetailPanel
            entity={selectedEntity}
            queryJurisdiction={response.query.jurisdiction}
            searchId={response.search_id}
            enrichmentAvailable={response.meta.enrichment_available}
            onBack={() => setSelectedIndex(null)}
          />
        ) : response && !pending && response.results.length === 0 ? (
          <>
            <MetaBlock meta={response.meta} />
            <p className="empty">No results.</p>
          </>
        ) : response && response.results.length > 0 ? (
          <>
            <MetaBlock meta={response.meta} />
            <div className="results-heading">
              <div>
                <span>{response.results.length} matched entities</span>
                <strong>{response.query.term}</strong>
              </div>
              <span>{response.query.jurisdiction}</span>
            </div>
            <ol className="result-list">
              {response.results.map((entity, i) => (
                <ResultRow
                  key={entity.result_token}
                  entity={entity}
                  showJurisdiction={entity.jurisdiction !== response.query.jurisdiction}
                  onActivate={() => onResultActivate(i)}
                />
              ))}
            </ol>
          </>
        ) : !pending && !error ? (
          <p className="empty">Enter a name above to search.</p>
        ) : null}
        </section>
      </main>
    </div>
  );
}

export default App;
